import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import AdminLayout from "@/components/Layout";
import {
  Activity, Users, Tv, Cpu, HardDrive, Wifi, WifiOff,
  RadioTower, RefreshCw, Loader2, Server, AlertCircle,
  Zap, ZapOff, PlayCircle, Copy, CheckCircle2, XCircle,
  Database, Clock,
} from "lucide-react";

/**
 * /admin/streams/live — anlık yayın durumu + buffer kontrol paneli
 */
export default function StreamsLive() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState("");
  const [copiedId, setCopiedId] = useState("");

  const load = useCallback(async () => {
    setRefreshing(true); setError("");
    try {
      const r = await api.get("/streams/live");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Veri çekilemedi.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (!autoRefresh) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load, autoRefresh]);

  async function toggleBuffer(ch, enabled) {
    setBusyId(ch.id); setError("");
    try {
      await api.post(`/channels/${ch.id}/buffer`, { enabled });
      await load();
    } catch (e) {
      setError(`${ch.name}: ${e?.response?.data?.detail || "Buffer toggle hatası"}`);
    } finally {
      setBusyId("");
    }
  }

  async function verify(ch) {
    setVerifyBusy(ch.id); setVerifyResult(null); setError("");
    try {
      const r = await api.get(`/engine/verify/${ch.id}`);
      setVerifyResult({ channel_id: ch.id, channel_name: ch.name, ...r.data });
      await load();
    } catch (e) {
      setVerifyResult({
        channel_id: ch.id,
        channel_name: ch.name,
        ok: false,
        error: e?.response?.data?.detail || "Doğrulama başarısız.",
      });
    } finally {
      setVerifyBusy("");
    }
  }

  function copyTestLink(ch) {
    const base = (window.location.origin || "").replace(/\/$/, "");
    const url = `${base}/api/live/${ch.id}.m3u8?mode=buffer`;
    navigator.clipboard?.writeText(url).then(() => {
      setCopiedId(ch.id);
      setTimeout(() => setCopiedId(""), 1500);
    });
  }

  const summary = data?.summary;
  const channels = data?.channels || [];
  const nodes = data?.nodes || [];
  const activeChannels = channels.filter(c => c.viewers > 0 || c.engine);
  const idleChannels = channels.filter(c => !(c.viewers > 0 || c.engine));
  const totalBufferedMin = ((summary?.total_buffered_seconds || 0) / 60).toFixed(1);

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Activity className="text-amber-500" size={24} />
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="streams-live-title">Anlık Yayınlar</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                Aktif izleyiciler, edge node yükü ve FFmpeg buffer durumu
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 accent-amber-500"
                data-testid="streams-live-auto-refresh"
              />
              Otomatik yenile (5s)
            </label>
            <button
              onClick={load}
              disabled={refreshing}
              className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 disabled:opacity-60"
              data-testid="streams-live-refresh"
              type="button"
            >
              {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Yenile
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 mb-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-xs" data-testid="streams-live-error">
            <AlertCircle size={14} className="shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-32 text-gray-500">
            <Loader2 className="animate-spin mr-3" /> Yükleniyor…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
              <SummaryCard icon={Tv}         label="Aktif Kanal"    value={summary?.active_channels} subValue={summary?.total_channels} subLabel="toplam" accent="amber" testid="summary-active-channels" />
              <SummaryCard icon={Users}      label="İzleyici"       value={summary?.active_viewers}  accent="green" testid="summary-active-viewers" />
              <SummaryCard icon={Zap}        label="Buffer Aktif"   value={summary?.engine_running}  accent="purple" testid="summary-engine-running" />
              <SummaryCard icon={Clock}      label="Buffered (dk)"  value={totalBufferedMin}         accent="purple" testid="summary-buffered-minutes" />
              <SummaryCard icon={Server}     label="Online Node"    value={summary?.online_nodes}    subValue={summary?.total_nodes} subLabel="kayıtlı" accent="amber" testid="summary-online-nodes" />
              <SummaryCard icon={Cpu}        label="Transcode"      value={(data?.transcodes || []).length} accent="amber" testid="summary-active-transcodes" />
            </div>

            {verifyResult && (
              <VerifyBanner result={verifyResult} onClose={() => setVerifyResult(null)} />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <RadioTower size={18} className="text-amber-400" /> Aktif & Buffer'da
                  </h2>
                  <span className="text-[10px] font-mono uppercase text-gray-500">son 30 saniye</span>
                </div>

                {activeChannels.length === 0 ? (
                  <div className="text-center py-8 text-gray-600 text-sm" data-testid="streams-live-empty">
                    <Tv className="mx-auto mb-3 opacity-30" size={32} />
                    Şu an aktif yayın veya buffer yok.
                    <span className="block text-[10px] mt-1">
                      Aşağıdaki kanal listesinden bir kanalı buffer moduna alabilirsiniz.
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {activeChannels.map(ch => (
                      <ChannelRow
                        key={ch.id} ch={ch}
                        busy={busyId === ch.id}
                        verifyBusy={verifyBusy === ch.id}
                        copied={copiedId === ch.id}
                        onToggle={(en) => toggleBuffer(ch, en)}
                        onVerify={() => verify(ch)}
                        onCopy={() => copyTestLink(ch)}
                      />
                    ))}
                  </div>
                )}

                {idleChannels.length > 0 && (
                  <details className="mt-4 group">
                    <summary className="text-[11px] text-gray-500 hover:text-amber-400 cursor-pointer select-none" data-testid="streams-live-idle-toggle">
                      ▶ Boştaki kanallar ({idleChannels.length}) — buffer'a alınabilir
                    </summary>
                    <div className="mt-3 space-y-2 max-h-[400px] overflow-y-auto pr-1">
                      {idleChannels.map(ch => (
                        <ChannelRow
                          key={ch.id} ch={ch}
                          busy={busyId === ch.id}
                          verifyBusy={verifyBusy === ch.id}
                          copied={copiedId === ch.id}
                          onToggle={(en) => toggleBuffer(ch, en)}
                          onVerify={() => verify(ch)}
                          onCopy={() => copyTestLink(ch)}
                          dim
                        />
                      ))}
                    </div>
                  </details>
                )}
              </div>

              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
                <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                  <Server size={18} className="text-amber-400" /> Edge Nodes
                </h2>
                {nodes.length === 0 ? (
                  <div className="text-center py-8 text-gray-600 text-xs" data-testid="streams-live-no-nodes">
                    <WifiOff className="mx-auto mb-2 opacity-30" size={24} />
                    Hiç edge node kayıtlı değil.<br />
                    <span className="text-[10px] block mt-2">
                      Yayınlar şu an panel container'ı üzerinden serve ediliyor.
                    </span>
                  </div>
                ) : (
                  <div className="space-y-3">{nodes.map(n => <NodeCard key={n.id} n={n} />)}</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, subValue, subLabel, accent, testid }) {
  const colors = { amber: "text-amber-400", green: "text-green-400", purple: "text-purple-300", default: "text-white" };
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 text-gray-500">
        <Icon size={14} />
        <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className={`text-3xl font-bold ${colors[accent] || colors.default}`}>{value ?? 0}</span>
        {subValue !== undefined && subValue !== null && (
          <span className="text-xs text-gray-600">/ {subValue} {subLabel}</span>
        )}
      </div>
    </div>
  );
}

function ChannelRow({ ch, busy, verifyBusy, copied, onToggle, onVerify, onCopy, dim }) {
  const isBuffered = !!ch.engine && ch.engine.alive;
  const segs = ch.segment_count || 0;
  const bufMin = ((ch.buffered_seconds || 0) / 60).toFixed(1);
  const ringClass = isBuffered ? "border-purple-500/40 hover:border-purple-500/60"
                               : ch.viewers > 0 ? "border-amber-500/30 hover:border-amber-500/50"
                               : "border-slate-800/80 hover:border-slate-700";
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg bg-slate-950/60 border ${ringClass} transition ${dim ? "opacity-80" : ""}`}
      data-testid={`live-channel-${ch.id}`}
    >
      <div className="w-9 h-9 rounded-lg bg-slate-900 grid place-items-center shrink-0 overflow-hidden">
        {ch.logo_url
          ? <img src={ch.logo_url} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          : <Tv className="text-gray-600" size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-bold text-white truncate min-w-0">{ch.name}</div>
          {ch.buffer_enabled && (
            <span
              className="shrink-0 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-200 border border-purple-500/40"
              data-testid={`buffer-pill-${ch.id}`}
            >
              BUFFER
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-gray-500">
          <span>
            {ch.status === "online" ? <span className="text-green-400">●</span> : <span className="text-amber-400">●</span>} {ch.status}
          </span>
          {isBuffered && (
            <span className="text-purple-300 flex items-center gap-1" data-testid={`live-channel-${ch.id}-cache`}>
              <Database size={10} /> {segs} seg · {bufMin} dk
            </span>
          )}
        </div>
      </div>

      {ch.viewers > 0 && (
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/15 border border-amber-500/40">
          <Users size={12} className="text-amber-400" />
          <span className="text-sm font-bold text-amber-300 tabular-nums">{ch.viewers}</span>
        </div>
      )}

      <button
        onClick={() => onToggle(!ch.buffer_enabled)}
        disabled={busy || !ch.has_stream_url}
        className={`px-2.5 py-1 rounded-lg text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-50 transition ${
          ch.buffer_enabled
            ? "bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 border border-purple-500/40"
            : "bg-slate-800 hover:bg-slate-700 text-gray-300 border border-slate-700"
        }`}
        title={ch.buffer_enabled ? "Buffer'ı kapat" : "FFmpeg buffer başlat"}
        data-testid={`buffer-toggle-${ch.id}`}
        type="button"
      >
        {busy ? <Loader2 size={12} className="animate-spin" />
          : ch.buffer_enabled ? <ZapOff size={12} /> : <Zap size={12} />}
        {ch.buffer_enabled ? "Durdur" : "Başlat"}
      </button>

      <button
        onClick={onVerify}
        disabled={verifyBusy || !ch.has_stream_url}
        className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-800 hover:bg-slate-700 text-gray-300 border border-slate-700 flex items-center gap-1.5 disabled:opacity-50 transition"
        title="Sunucu tarafında VLC simülasyonu (playlist + ilk segment fetch)"
        data-testid={`buffer-verify-${ch.id}`}
        type="button"
      >
        {verifyBusy ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
        Test Et
      </button>

      <button
        onClick={onCopy}
        className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-gray-300 border border-slate-700 transition"
        title="Buffer URL'sini panoya kopyala"
        data-testid={`buffer-copy-${ch.id}`}
        type="button"
      >
        {copied ? <CheckCircle2 size={12} className="text-green-400" /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function VerifyBanner({ result, onClose }) {
  const ok = result.ok;
  return (
    <div
      className={`flex items-start gap-3 p-4 mb-4 rounded-xl border ${
        ok ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"
      }`}
      data-testid={`verify-banner-${ok ? "ok" : "fail"}`}
    >
      {ok ? <CheckCircle2 className="text-green-400 shrink-0 mt-0.5" size={18} />
        : <XCircle className="text-red-400 shrink-0 mt-0.5" size={18} />}
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold ${ok ? "text-green-300" : "text-red-300"}`}>
          {ok ? "VLC simülasyonu başarılı" : "VLC simülasyonu başarısız"}
          <span className="text-gray-400 font-normal ml-2">— {result.channel_name}</span>
        </div>
        {ok ? (
          <div className="text-[11px] font-mono text-gray-400 mt-1.5 space-y-0.5">
            <div>playlist: <span className="text-green-300">HTTP {result.playlist_status}</span> · {result.playlist_bytes} B</div>
            <div className="truncate">↳ <span className="text-amber-300">{result.playlist_url}</span></div>
            <div>segment: <span className="text-green-300">HTTP {result.first_segment_status}</span> · {Math.round((result.first_segment_bytes || 0) / 1024)} KB · {result.first_segment_ctype}</div>
          </div>
        ) : (
          <div className="text-[11px] text-red-300/90 mt-1">
            {result.detail || result.error || "Bilinmeyen hata"} {result.stage ? `(stage=${result.stage})` : ""}
          </div>
        )}
      </div>
      <button onClick={onClose} className="text-gray-500 hover:text-white text-xs" type="button" data-testid="verify-banner-close">Kapat</button>
    </div>
  );
}

function NodeCard({ n }) {
  const statusColor =
    n.status === "online" ? "border-green-500/30 bg-green-500/5"
      : n.status === "degraded" ? "border-amber-500/30 bg-amber-500/5"
        : "border-red-500/30 bg-red-500/5";
  const statusIcon =
    n.status === "online" ? <Wifi className="text-green-400" size={14} />
      : n.status === "degraded" ? <Wifi className="text-amber-400" size={14} />
        : <WifiOff className="text-red-400" size={14} />;

  return (
    <div className={`p-3 rounded-lg border ${statusColor}`} data-testid={`live-node-${n.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {statusIcon}
          <span className="text-sm font-bold text-white truncate">{n.name}</span>
        </div>
        <span className="text-[9px] font-mono uppercase text-gray-500">{n.location}</span>
      </div>
      <div className="text-[10px] font-mono text-gray-500 mb-2 truncate">{n.ip}</div>
      <div className="grid grid-cols-3 gap-2 text-[10px]">
        <Mini icon={Cpu} label="CPU" value={`${n.cpu.toFixed(0)}%`} />
        <Mini icon={HardDrive} label="RAM" value={`${n.mem.toFixed(0)}%`} />
        <Mini icon={RadioTower} label="Stream" value={`${n.streams}/${n.max_capacity}`} />
      </div>
      <div className="mt-2.5">
        <div className="h-1.5 bg-slate-950 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${
              n.load_pct > 85 ? "bg-red-500" : n.load_pct > 60 ? "bg-amber-500" : "bg-green-500"
            }`}
            style={{ width: `${Math.min(100, n.load_pct)}%` }}
          />
        </div>
        <div className="text-[9px] font-mono uppercase text-gray-500 mt-1 flex justify-between">
          <span>Yük</span><span>{n.load_pct}%</span>
        </div>
      </div>
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-950 rounded p-1.5">
      <div className="flex items-center gap-1 text-gray-600 mb-0.5">
        <Icon size={9} />
        <span className="uppercase text-[8px] tracking-wider">{label}</span>
      </div>
      <div className="text-white font-mono">{value}</div>
    </div>
  );
}
